import { PrinterOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Space, Table, Tag, Typography } from "antd";
import { useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api } from "../api/client";
import { DocumentViewer } from "../components/DocumentViewer";
import { LoadProblem } from "../components/LoadProblem";
import { PageHeader } from "../components/PageHeader";

interface Line {
  code: string;
  name: string;
  quantity: string;
  batch?: string | null;
  expiry_date?: string | null;
  serials?: string[];
  serials_counted?: number;
}
interface Node {
  id: string;
  code: string;
  type: string;
  container: boolean;
  nominal?: number | null;
  lines: Line[];
  nested: Node[];
  total: string;
}
interface Snapshot {
  entity_type: string;
  entity_id: string;
  built_at: string;
  header: { container_code: string; container_kind: string; container_status: string; warehouse?: string | null; location_path?: string[]; documents: { kind: string; number: string; id: string }[]; partner?: string | null };
  tree: Node;
  totals: { by_object: { code: string; quantity: string }[]; quantity: string; containers: number; weight_computed?: string | null; weight_note: string };
}
interface Version {
  id: string;
  number: string;
  version: number;
  status: string;
  created_at: string;
  created_by?: string | null;
  note?: string | null;
  snapshot: Snapshot;
  changed_since?: boolean | null;
  warning?: string | null;
}
interface VersionRow {
  id: string;
  number: string;
  version: number;
  status: string;
  created_at: string;
  created_by?: string | null;
  current: boolean;
}

const STATUS: Record<string, string> = { draft: "черновик", closed: "закрыт", shipped: "отгружен" };

function flatten(node: Node, out: { key: string; container: string; line: Line }[] = []): typeof out {
  for (const line of node.lines) out.push({ key: `${node.id}-${line.code}-${line.batch ?? ""}`, container: node.code, line });
  for (const child of node.nested) flatten(child, out);
  return out;
}

function Tree({ node }: { node: Node }) {
  return (
    <ul style={{ margin: "4px 0 4px 16px", padding: 0, listStyle: "none" }}>
      <li>
        <b>{node.code}</b> <Typography.Text type="secondary">{node.type}</Typography.Text> — всего {node.total}
        {node.nominal ? <Typography.Text type="secondary"> · номинал {node.nominal}</Typography.Text> : null}
        {node.lines.map((l) => (
          <div key={`${l.code}-${l.batch ?? ""}`} style={{ marginLeft: 16 }}>
            {l.code} {l.name}: <b>{l.quantity}</b>
            {l.batch ? ` · партия ${l.batch}` : ""}
            {l.serials_counted ? ` · экземпляров ${l.serials_counted}` : ""}
          </div>
        ))}
        {node.nested.map((c) => (
          <Tree key={c.id} node={c} />
        ))}
      </li>
    </ul>
  );
}

/** A saved packing list version opened by its number («PL-000001», «PL-000001/2») with the other versions. */
export function PackingListPage() {
  const { number = "" } = useParams();
  const [params] = useSearchParams();
  const version = params.get("version");
  // KZ-DOC UI03: the saved version prints from its stored PDF; the HTML form stays reachable from the viewer
  const [viewing, setViewing] = useState(false);
  const current = useQuery({
    queryKey: ["packing-list", number, version],
    queryFn: () => api.get<Version>(`/packing-lists/by-number/${encodeURIComponent(number)}`, { version: version ?? undefined }),
  });
  const data = current.data;
  const versions = useQuery({
    queryKey: ["packing-list-versions", data?.snapshot.entity_type, data?.snapshot.entity_id],
    queryFn: () => api.get<{ versions: VersionRow[] }>(`/packing-lists/${data!.snapshot.entity_type}/${data!.snapshot.entity_id}`),
    enabled: !!data,
  });
  if (current.isError) return <LoadProblem error={current.error} />;
  if (!data) return <Typography.Text type="secondary">Загрузка…</Typography.Text>;
  const s = data.snapshot;
  const rows = flatten(s.tree);
  const containerPath = s.entity_type === "cargo_unit" ? `/cargo_units/${s.entity_id}` : `/locations/${s.entity_id}`;
  return (
    <div>
      <PageHeader
        title={`Упаковочный лист ${data.number}, версия ${data.version}`}
        subtitle={`${s.header.container_kind} ${s.header.container_code} · статус листа: ${STATUS[data.status] ?? data.status} · ${new Date(data.created_at).toLocaleString("ru-RU")}${data.created_by ? ` · ${data.created_by}` : ""}`}
        extra={
          <Space wrap>
            <Link to={containerPath}>
              <Button>Открыть контейнер</Button>
            </Link>
            <Button icon={<PrinterOutlined />} onClick={() => setViewing(true)} data-testid="packing-list-print">
              Печать этой версии
            </Button>
          </Space>
        }
      />
      {data.warning && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={data.warning} description="Эта версия сохранена как снимок и не меняется. Актуальный состав — по коду контейнера." />}
      <div className="wms-card" style={{ padding: 16, marginBottom: 16 }}>
        <Space wrap size={[24, 8]}>
          <span>Склад: <b>{s.header.warehouse ?? "—"}</b></span>
          <span>Место: <b>{(s.header.location_path ?? []).join(" → ") || "—"}</b></span>
          <span>Документы: <b>{s.header.documents.map((d) => d.number).join(", ") || "—"}</b></span>
          <span>Получатель: <b>{s.header.partner ?? "—"}</b></span>
          <span>Всего единиц: <b>{s.totals.quantity}</b></span>
          <span>Контейнеров: <b>{s.totals.containers}</b></span>
          <span>Масса: <b>{s.totals.weight_computed ? `${s.totals.weight_computed} кг (${s.totals.weight_note})` : "не задана"}</b></span>
        </Space>
        <Typography.Title level={5} style={{ marginTop: 16 }}>Дерево вложенности</Typography.Title>
        <Tree node={s.tree} />
      </div>
      <div className="wms-card" style={{ padding: 16, marginBottom: 16 }}>
        <Table
          size="small"
          rowKey="key"
          pagination={false}
          dataSource={rows}
          columns={[
            { title: "Контейнер", dataIndex: "container", width: 160 },
            { title: "Товар", render: (_, r) => `${r.line.code} ${r.line.name}` },
            { title: "Партия", render: (_, r) => r.line.batch ?? "" , width: 120 },
            { title: "Годен до", render: (_, r) => r.line.expiry_date ?? "", width: 110 },
            { title: "Количество", render: (_, r) => r.line.quantity, align: "right", width: 110 },
            { title: "Серийные ID", render: (_, r) => (r.line.serials?.length ? `${r.line.serials.slice(0, 5).join(", ")}${r.line.serials.length > 5 ? ` … (${r.line.serials.length})` : ""}` : "") },
          ]}
        />
      </div>
      <div className="wms-card" style={{ padding: 16 }}>
        <Typography.Title level={5}>Версии</Typography.Title>
        <Table
          size="small"
          rowKey="id"
          pagination={false}
          loading={versions.isLoading}
          dataSource={versions.data?.versions ?? []}
          columns={[
            { title: "Версия", dataIndex: "version", width: 90, render: (v: number, r) => <Link to={`/packing-lists/${r.number}?version=${v}`}>v{v}</Link> },
            { title: "Статус", dataIndex: "status", width: 120, render: (v: string) => STATUS[v] ?? v },
            { title: "Сохранена", dataIndex: "created_at", render: (v: string) => new Date(v).toLocaleString("ru-RU") },
            { title: "Кем", dataIndex: "created_by", width: 140 },
            { title: "", dataIndex: "current", width: 160, render: (v: boolean) => (v ? <Tag color="green">совпадает с текущим</Tag> : <Tag>состав изменён</Tag>) },
          ]}
        />
      </div>
      {viewing && (
        <DocumentViewer
          open
          onClose={() => setViewing(false)}
          kind="packing_list"
          documentId={data.id}
          legacyForm="packing_list_version"
          legacyPath={`/print-forms/packing_list_version/${data.id}`}
          title={`Упаковочный лист ${data.number}, версия ${data.version}`}
        />
      )}
    </div>
  );
}
